import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Save, Trash2, Undo2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { itemService } from '@/services/itemService'
import { warehouseService } from '@/services/warehouseService'
import { batchItemService } from '@/services/batchItemService'
import type { BatchItemVm, BatchItemWhItemVm, ItemEntity, ItemLookupOption, Warehouse } from '@/api/types'
import type { OpeningStockLineUi } from '../types'
import { newOpeningLineUi } from '../types'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function sumLines(lines: Array<{ isDeleted: boolean; wareHouseId: number; availableQuantity: number; damagedStock: number; expiredStock: number; holdStock: number }>) {
  return lines.reduce(
    (acc, l) => {
      if (l.isDeleted) return acc
      if (!l.wareHouseId) return acc
      acc.availableQuantity += Number(l.availableQuantity || 0)
      acc.damagedStock += Number(l.damagedStock || 0)
      acc.expiredStock += Number(l.expiredStock || 0)
      acc.holdStock += Number(l.holdStock || 0)
      return acc
    },
    { availableQuantity: 0, damagedStock: 0, expiredStock: 0, holdStock: 0 }
  )
}

export const ItemBatchEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const isNew = !id || id === 'new'
  const numericId = Number(id || 0)
  const isEditMode = !isNew && Number.isFinite(numericId) && numericId > 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [items, setItems] = useState<ItemEntity[]>([])
  const [categories, setCategories] = useState<ItemLookupOption[]>([])
  const [subCategories, setSubCategories] = useState<ItemLookupOption[]>([])
  const [uoms, setUoms] = useState<ItemLookupOption[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])

  const [selectedItem, setSelectedItem] = useState<ItemEntity | null>(null)

  const [form, setForm] = useState<BatchItemVm>({
    id: 0,
    code: '',
    reference: '',
    itemId: 0,
    batchDate: todayIso(),
    availableQuantity: 0,
    damagedStock: 0,
    holdStock: 0,
    expiredStock: 0,
    expiryDate: '',
    unitSellingPrice: 0,
    unitCostPrice: 0,
    isTaxable: false,
    tax: 0,
    availabilityStatus: '',
    branchId: branchId || 0,
    whItems: [],
  })

  const [openingLines, setOpeningLines] = useState<OpeningStockLineUi[]>(() =>
    Array.from({ length: 5 }, () => newOpeningLineUi())
  )

  useEffect(() => {
    const totals = sumLines(openingLines)
    setForm((prev) => ({
      ...prev,
      availableQuantity: totals.availableQuantity,
      damagedStock: totals.damagedStock,
      expiredStock: totals.expiredStock,
      holdStock: totals.holdStock,
    }))
  }, [openingLines])

  useEffect(() => {
    setForm((prev) => ({ ...prev, branchId: branchId || 0 }))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        setLoading(true)
        const [itemsRes, cats, subs, uomLookups, whs] = await Promise.all([
          itemService.getAllItems(branchId, 1, 2000),
          itemService.getCategories(branchId),
          itemService.getSubCategories(branchId),
          itemService.getMaterialUOMsLookups(),
          warehouseService.getAllWarehouses(branchId),
        ])
        setItems(itemsRes.items || [])
        setCategories(cats || [])
        setSubCategories(subs || [])
        setUoms(uomLookups || [])
        setWarehouses(whs || [])
      } catch {
        setItems([])
        setCategories([])
        setSubCategories([])
        setUoms([])
        setWarehouses([])
      } finally {
        setLoading(false)
      }
    }
    void loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || !isEditMode) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await batchItemService.getById(numericId)
        setForm({
          ...data,
          branchId: data.branchId || branchId,
        })
        const it = await itemService.getItemById(data.itemId, branchId)
        setSelectedItem(it)
        let whLines: BatchItemWhItemVm[] = []
        try {
          whLines = await batchItemService.getWhItemsByBatchId(data.id)
        } catch {
          whLines = data.whItems || []
        }
        setOpeningLines(
          whLines.length > 0
            ? whLines.map((x) =>
                newOpeningLineUi({
                  id: x.id,
                  itemId: x.itemId,
                  batchItemId: x.batchItemId,
                  wareHouseId: x.wareHouseId,
                  shelfId: x.shelfId || '',
                  availableQuantity: Number(x.availableQuantity || 0),
                  damagedStock: Number(x.damagedStock || 0),
                  expiredStock: Number(x.expiredStock || 0),
                  holdStock: Number(x.holdStock || 0),
                  isDeleted:
                    String(x.isDeleted || '').toLowerCase() === 'true' ||
                    String(x.isDeleted || '').toLowerCase() === '1',
                })
              )
            : Array.from({ length: 5 }, () => newOpeningLineUi())
        )
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('itemBatches.failedToLoadOne'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, isEditMode, numericId, t])

  const itemOptions = useMemo((): SearchableSelectOption[] => {
    return items.map((it) => {
      const labelName = language === 'ar' ? it.itemNameAr || it.itemNameEn : it.itemNameEn || it.itemNameAr
      return { value: String(it.itemId), label: `${labelName || it.itemCode}` }
    })
  }, [items, language])

  const categoryById = useMemo(() => {
    const map: Record<number, string> = {}
    categories.forEach((c) => {
      map[c.id] = language === 'ar' ? c.nameAr || c.nameEn : c.nameEn || c.nameAr
    })
    return map
  }, [categories, language])

  const subCategoryById = useMemo(() => {
    const map: Record<number, string> = {}
    subCategories.forEach((c) => {
      map[c.id] = language === 'ar' ? c.nameAr || c.nameEn : c.nameEn || c.nameAr
    })
    return map
  }, [subCategories, language])

  const uomById = useMemo(() => {
    const map: Record<number, string> = {}
    uoms.forEach((c) => {
      map[c.id] = language === 'ar' ? c.nameAr || c.nameEn : c.nameEn || c.nameAr
    })
    return map
  }, [uoms, language])

  const handleBack = () => navigate('/inventory/item-batches')

  const addOpeningRows = (n: number) => {
    setOpeningLines((prev) => [...prev, ...Array.from({ length: n }, () => newOpeningLineUi())])
  }

  const updateOpening = (index: number, patch: Partial<OpeningStockLineUi>) => {
    setOpeningLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const toggleDeleteOpening = (index: number) => {
    setOpeningLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], isDeleted: !next[index].isDeleted }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) {
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!form.itemId) {
      toast.error(t('itemBatches.validationItem'))
      return
    }

    try {
      setSaving(true)
      const linesToSave = openingLines.filter((l) => {
        const whOk = Number(l.wareHouseId || 0) > 0
        if (!whOk) return false
        const qtySum =
          Number(l.availableQuantity || 0) +
          Number(l.damagedStock || 0) +
          Number(l.expiredStock || 0) +
          Number(l.holdStock || 0)
        const hasAnyQty = qtySum !== 0
        const isExisting = Number(l.id || 0) > 0
        return hasAnyQty || l.isDeleted || isExisting
      })

      // Build the exact save structure (same keys) for console + API.
      const payloadExact: BatchItemVm = {
        id: isEditMode ? Number(form.id || 0) : 0,
        code: String(form.code || ''),
        reference: String(form.reference || ''),
        itemId: Number(form.itemId || 0),
        batchDate: String(form.batchDate || ''),
        availableQuantity: Number(form.availableQuantity || 0),
        damagedStock: Number(form.damagedStock || 0),
        holdStock: Number(form.holdStock || 0),
        expiredStock: Number(form.expiredStock || 0),
        expiryDate: String(form.expiryDate || ''),
        unitSellingPrice: Number(form.unitSellingPrice || 0),
        unitCostPrice: Number(form.unitCostPrice || 0),
        isTaxable: Boolean(form.isTaxable),
        tax: Number(form.tax || 0),
        availabilityStatus: String(form.availabilityStatus || ''),
        branchId: Number(branchId || 0),
        whItems: linesToSave.map((l) => ({
          id: Number(l.id || 0),
          itemId: Number(form.itemId || 0),
          batchItemId: isEditMode ? Number(form.id || 0) : 0,
          wareHouseId: Number(l.wareHouseId || 0),
          shelfId: String(l.shelfId || ''),
          availableQuantity: Number(l.availableQuantity || 0),
          damagedStock: Number(l.damagedStock || 0),
          expiredStock: Number(l.expiredStock || 0),
          holdStock: Number(l.holdStock || 0),
          isDeleted: l.isDeleted ? 'true' : 'false',
        })),
      }

      console.log('[ItemBatchEdit] save payload (exact)')
      console.log(JSON.stringify(payloadExact, null, 2))

      if (isEditMode) await batchItemService.update(payloadExact)
      else await batchItemService.save(payloadExact)

      toast.success(t('common.saved'))
      navigate('/inventory/item-batches')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('itemBatches.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('itemBatches.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('itemBatches.formSubtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('itemBatches.sectionItem')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>{t('itemBatches.item')} *</Label>
                <SearchableSelect
                  options={itemOptions}
                  value={form.itemId ? String(form.itemId) : ''}
                  onValueChange={async (v) => {
                    const nextId = Number(v || 0)
                    setForm((p) => ({ ...p, itemId: nextId }))
                    if (!branchId || !nextId) {
                      setSelectedItem(null)
                      return
                    }
                    try {
                      const it = await itemService.getItemById(nextId, branchId)
                      setSelectedItem(it)
                    } catch {
                      setSelectedItem(null)
                    }
                  }}
                  placeholder={t('itemBatches.selectItem')}
                  searchPlaceholder={t('itemBatches.searchItem')}
                  className="w-full"
                  disabled={saving || loading || isEditMode}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-item-code">{t('itemBatches.itemCode')}</Label>
                <Input id="ib-item-code" value={selectedItem?.itemCode || ''} readOnly disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-item-name">{t('itemBatches.itemName')}</Label>
                <Input
                  id="ib-item-name"
                  value={
                    selectedItem
                      ? language === 'ar'
                        ? selectedItem.itemNameAr || selectedItem.itemNameEn
                        : selectedItem.itemNameEn || selectedItem.itemNameAr
                      : ''
                  }
                  readOnly
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ib-item-desc">{t('itemBatches.itemDescription')}</Label>
                <Input id="ib-item-desc" value={selectedItem?.description || ''} readOnly disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-item-category">{t('itemBatches.category')}</Label>
                <Input
                  id="ib-item-category"
                  value={
                    selectedItem
                      ? categoryById[selectedItem.itemCategoryId] || String(selectedItem.itemCategoryId || '')
                      : ''
                  }
                  readOnly
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-item-subcategory">{t('itemBatches.subCategory')}</Label>
                <Input
                  id="ib-item-subcategory"
                  value={
                    selectedItem
                      ? subCategoryById[selectedItem.subCategoryId] || String(selectedItem.subCategoryId || '')
                      : ''
                  }
                  readOnly
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ib-item-uom">{t('itemBatches.uom')}</Label>
                <Input
                  id="ib-item-uom"
                  value={selectedItem ? uomById[selectedItem.uom] || String(selectedItem.uom || '') : ''}
                  readOnly
                  disabled
                />
              </div>
              <div className="hidden md:block" aria-hidden />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('itemBatches.sectionBatch')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {isEditMode ? (
                <div className="space-y-2">
                  <Label htmlFor="ib-batch-code">{t('itemBatches.batchCode')}</Label>
                  <Input id="ib-batch-code" value={form.code} readOnly disabled />
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="ib-batch-date">{t('itemBatches.batchDate')}</Label>
                <Input
                  id="ib-batch-date"
                  type="date"
                  value={form.batchDate || ''}
                  onChange={(e) => setForm((p) => ({ ...p, batchDate: e.target.value }))}
                  disabled={saving || loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-batch-ref">{t('itemBatches.batchReference')}</Label>
                <Input
                  id="ib-batch-ref"
                  value={form.reference || ''}
                  onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                  disabled={saving || loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ib-avail">{t('itemBatches.availableStockQty')}</Label>
                <Input
                  id="ib-avail"
                  type="number"
                  min={0}
                  value={form.availableQuantity}
                  readOnly
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-dmg">{t('itemBatches.damagedStockQty')}</Label>
                <Input
                  id="ib-dmg"
                  type="number"
                  min={0}
                  value={form.damagedStock}
                  readOnly
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-exp">{t('itemBatches.expiredStockQty')}</Label>
                <Input
                  id="ib-exp"
                  type="number"
                  min={0}
                  value={form.expiredStock}
                  readOnly
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ib-hold">{t('itemBatches.holdStockQty')}</Label>
                <Input
                  id="ib-hold"
                  type="number"
                  min={0}
                  value={form.holdStock}
                  readOnly
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-cost">{t('itemBatches.unitCostPrice')}</Label>
                <Input
                  id="ib-cost"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unitCostPrice}
                  onChange={(e) => setForm((p) => ({ ...p, unitCostPrice: Number(e.target.value) || 0 }))}
                  disabled={saving || loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ib-sell">{t('itemBatches.unitSellingPrice')}</Label>
                <Input
                  id="ib-sell"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unitSellingPrice}
                  onChange={(e) => setForm((p) => ({ ...p, unitSellingPrice: Number(e.target.value) || 0 }))}
                  disabled={saving || loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ib-expiry">{t('itemBatches.expiryDate')}</Label>
                <Input
                  id="ib-expiry"
                  type="date"
                  value={form.expiryDate || ''}
                  onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))}
                  disabled={saving || loading}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('itemBatches.sectionOpeningStock')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addOpeningRows(1)}
                disabled={saving || loading || isEditMode}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('itemBatches.addRow')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addOpeningRows(3)} disabled={saving || loading || isEditMode}>
                {t('itemBatches.add3Rows')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addOpeningRows(5)} disabled={saving || loading || isEditMode}>
                {t('itemBatches.add5Rows')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5 hover:bg-primary/5">
                    <TableHead className="font-semibold text-primary">{t('itemBatches.opening.warehouse')}</TableHead>
                    <TableHead className="text-center font-semibold text-primary">{t('itemBatches.opening.available')}</TableHead>
                    <TableHead className="text-center font-semibold text-primary">{t('itemBatches.opening.damaged')}</TableHead>
                    <TableHead className="text-center font-semibold text-primary">{t('itemBatches.opening.expired')}</TableHead>
                    <TableHead className="text-center font-semibold text-primary">{t('itemBatches.opening.hold')}</TableHead>
                    {!isEditMode ? (
                      <TableHead className="w-[72px] text-center font-semibold text-primary">{t('common.delete')}</TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {openingLines.map((line, index) => (
                    <TableRow
                      key={line.key}
                      className={line.isDeleted ? 'bg-destructive/10 hover:bg-destructive/10' : undefined}
                    >
                      <TableCell className="align-top">
                        <Select
                          value={String(line.wareHouseId || 0)}
                          onValueChange={(v) => updateOpening(index, { wareHouseId: Number(v || 0) })}
                          disabled={saving || loading || line.isDeleted || isEditMode}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('itemBatches.opening.selectWarehouse')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">{t('itemBatches.opening.selectWarehouse')}</SelectItem>
                            {warehouses.map((w) => (
                              <SelectItem key={w.id} value={String(w.id)}>
                                {w.name || w.code || String(w.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={0}
                          className="text-center"
                          value={line.availableQuantity}
                          onChange={(e) => updateOpening(index, { availableQuantity: Number(e.target.value) || 0 })}
                          disabled={saving || loading || line.isDeleted || isEditMode}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={0}
                          className="text-center"
                          value={line.damagedStock}
                          onChange={(e) => updateOpening(index, { damagedStock: Number(e.target.value) || 0 })}
                          disabled={saving || loading || line.isDeleted || isEditMode}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={0}
                          className="text-center"
                          value={line.expiredStock}
                          onChange={(e) => updateOpening(index, { expiredStock: Number(e.target.value) || 0 })}
                          disabled={saving || loading || line.isDeleted || isEditMode}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={0}
                          className="text-center"
                          value={line.holdStock}
                          onChange={(e) => updateOpening(index, { holdStock: Number(e.target.value) || 0 })}
                          disabled={saving || loading || line.isDeleted || isEditMode}
                        />
                      </TableCell>
                      {!isEditMode ? (
                        <TableCell className="text-center align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={
                              line.isDeleted
                                ? 'text-muted-foreground hover:bg-muted/40'
                                : 'text-destructive hover:bg-destructive/10'
                            }
                            onClick={() => toggleDeleteOpening(index)}
                            disabled={saving || loading}
                          >
                            {line.isDeleted ? <Undo2 className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleBack} disabled={saving || loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saving || loading}>
            <Save className="mr-2 h-4 w-4" />
            {t('itemBatches.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
